import { Aperture, ListTree, Palette, Sparkles, WandSparkles } from "lucide-react";
import {
  PromptEnhancerResult,
  PromptEnhancerToggle,
  type PromptEnhancerController,
} from "@/components/PromptEnhancer";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  directionCameraLabel,
  directionLookLabel,
  type DirectionSelections,
} from "@/components/studio/studioConfig";

export function StudioDirectorPanel({
  open,
  onClose,
  prompt,
  directions,
  modality,
  enhancerSupported,
  enhancerEnabled,
  enhancerSaving,
  enhancer,
  onToggleEnhancer,
  onEnhance,
  onApplyPrompt,
  onOpenDirections,
}: {
  open: boolean;
  onClose: () => void;
  prompt: string;
  directions: DirectionSelections;
  modality: "image" | "video" | "audio";
  enhancerSupported: boolean;
  enhancerEnabled: boolean;
  enhancerSaving: boolean;
  enhancer: PromptEnhancerController;
  onToggleEnhancer: (checked: boolean) => void;
  onEnhance: () => void;
  onApplyPrompt: (value: string) => void;
  onOpenDirections: () => void;
}) {
  const lookLabel = directionLookLabel(directions);
  const cameraLabel = directionCameraLabel(directions, modality);
  const seedPrompt = () => {
    if (prompt.trim()) return;
    onApplyPrompt("Subject and setting: \nAction and story beat: \nComposition and camera: \nLighting, texture and visual mood: \nMust preserve or avoid: ");
  };
  const addShotBeats = () => {
    const addition = "\n\nShot beats:\n1. Establish the subject and environment.\n2. Reveal the primary action with clear visual continuity.\n3. Land on the intended final composition.";
    onApplyPrompt(`${prompt.trim()}${addition}`.trim());
  };

  return (
    <Dialog open={open} onClose={onClose} title="AI Director" placement="right" className="sm:max-w-md">
      <div className="flex-1 overflow-y-auto p-4 sm:p-5">
        <div className="rounded-xl border border-studio-accent/25 bg-studio-accent-soft p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-studio-panel text-studio-accent"><WandSparkles className="h-4 w-4" /></span>
            <div>
              <p className="text-sm font-semibold text-studio-ink">Direct the active workflow</p>
              <p className="mt-1 text-[11px] leading-4 text-studio-muted">Camera, look and prompt actions update the same composer. Generation stays under your control.</p>
            </div>
          </div>
        </div>

        <section className="mt-5">
          <h3 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-studio-muted">Director actions</h3>
          <div className="mt-2.5 grid grid-cols-2 gap-2">
            <DirectorAction icon={Aperture} title="Pick a camera" detail={cameraLabel} onClick={onOpenDirections} />
            <DirectorAction icon={Palette} title="Set the look" detail={lookLabel} onClick={onOpenDirections} />
            <DirectorAction icon={Sparkles} title="Structure prompt" detail={prompt.trim() ? "Prompt already started" : "Add a production template"} onClick={seedPrompt} disabled={Boolean(prompt.trim())} />
            <DirectorAction icon={ListTree} title="Add shot beats" detail="Establish, action, finish" onClick={addShotBeats} disabled={!prompt.trim()} />
          </div>
        </section>

        <section className="mt-5 rounded-xl border border-studio-border bg-studio-card p-3.5">
          <h3 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-studio-muted">Current direction</h3>
          <dl className="mt-3 space-y-2 text-xs">
            <DirectionRow label="Genre" value={directions.genre} />
            <DirectionRow label="Look" value={lookLabel} />
            <DirectionRow label="Camera" value={cameraLabel} />
          </dl>
          <button type="button" onClick={onOpenDirections} className="mt-3 min-h-10 w-full rounded-lg border border-studio-border bg-studio-panel px-3 text-[11px] font-semibold text-studio-ink hover:border-studio-accent/35">
            Edit creative direction
          </button>
        </section>

        {enhancerSupported ? (
          <section className="mt-5 rounded-2xl border border-studio-border bg-studio-card p-3.5">
            <PromptEnhancerToggle
              id="director-enhance-before-generation"
              checked={enhancerEnabled}
              disabled={enhancerSaving}
              onCheckedChange={onToggleEnhancer}
            />
            {enhancerEnabled && !enhancer.result && (
              <div className="mt-3 rounded-xl bg-studio-panel p-3">
                <p className="text-pretty text-[11px] leading-5 text-studio-muted">Creates an editable prompt immediately. It never starts video generation.</p>
                <Button className="mt-3 w-full" size="sm" loading={enhancer.loading === "enhance"} onClick={onEnhance}>
                  {enhancer.loading !== "enhance" && <Sparkles className="h-3.5 w-3.5" />} Improve prompt
                </Button>
              </div>
            )}
            {enhancerEnabled && <PromptEnhancerResult controller={enhancer} className="mt-3" idPrefix="director" />}
            {enhancer.error && !enhancer.result && <p role="alert" className="mt-3 text-xs text-studio-danger">{enhancer.error}</p>}
          </section>
        ) : (
          <div className="mt-5 rounded-xl border border-studio-border bg-studio-card p-3.5">
            <p className="flex items-center gap-2 text-xs font-semibold text-studio-ink"><Sparkles className="h-3.5 w-3.5 text-studio-accent" /> Prompt Enhancer</p>
            <p className="mt-1 text-[11px] leading-4 text-studio-muted">Prompt enhancement is available when a supported BytePlus workflow is selected.</p>
          </div>
        )}
      </div>
    </Dialog>
  );
}

function DirectorAction({ icon: Icon, title, detail, onClick, disabled = false }: { icon: typeof Aperture; title: string; detail: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="min-h-28 rounded-xl border border-studio-border bg-studio-card p-3 text-left transition-colors hover:border-studio-accent/35 hover:bg-studio-card-hover disabled:cursor-not-allowed disabled:opacity-45"
    >
      <Icon className="h-4 w-4 text-studio-accent" />
      <span className="mt-3 block text-xs font-semibold text-studio-ink">{title}</span>
      <span className="mt-1 block line-clamp-2 text-[10px] leading-4 text-studio-muted">{detail}</span>
    </button>
  );
}

function DirectionRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-studio-muted">{label}</dt>
      <dd className="truncate font-semibold text-studio-ink">{value}</dd>
    </div>
  );
}
